import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";

import type { CaseListFilters } from "@/api/endpoints/build-case-report-metadata";
import { backTargetFromLocation, withBackTarget } from "@/app/back-target";
import { useAuth } from "@/auth/use-auth";
import { caseActionPath, caseDetailPath, ROUTES } from "@/app/routes";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SavedViewBar } from "@/features/saved-views/saved-view-bar";

import { ActionRequiredSection } from "./action-required-section";
import { ApplicationFilters } from "./application-filters";
import { filtersToSearchParams, parseFiltersFromSearch } from "./filters-url";
import { ListPagination } from "./list-pagination";
import { MyApplicationTable } from "./my-application-table";
import { useActionRequired } from "./use-action-required";
import { useMyApplications } from "./use-my-applications";

const PER_PAGE = 20;

/**
 * 大項目2「マイ案件」。自分が起票した案件の一覧（issue #29 の確定仕様）。
 *
 * 絞り込みとページの正本は URL に置く（共有・リロード・戻るで同じ結果を再現するため）。
 * 起票フロー・詳細ドロワー・保存フィルタ（ビュー）は後続 PR。
 */
export function MyApplicationListPage() {
  const { t } = useTranslation(["domain", "common"]);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // 詳細から戻ったときに絞り込みが消えないよう、今いる画面を戻り先に載せる
  const backTo = backTargetFromLocation(location);
  const { filters, page } = parseFiltersFromSearch(searchParams);

  const applyFilters = useCallback(
    (next: CaseListFilters) => {
      // 条件が変わったらページは先頭へ戻す（空振りのページを見せない）
      setSearchParams(filtersToSearchParams(next, 1), { replace: true });
    },
    [setSearchParams],
  );

  const applyPage = useCallback(
    (next: number) => {
      setSearchParams(filtersToSearchParams(filters, next));
    },
    [filters, setSearchParams],
  );

  const { data, isPending, isError } = useMyApplications({
    filters,
    page,
    perPage: PER_PAGE,
  });
  // 要対応は一覧の絞り込み・ページとは独立に取る（条件で内容が変わると誤解を招く）
  // 要対応も対象範囲に追従する（関与モードでは自分宛の承認待ちも出す）
  const actionRequired = useActionRequired({ involved: filters.involved });

  const openCase = useCallback(
    (caseId: number) => {
      const target = data?.items.find((item) => item.caseId === caseId);
      if (target === undefined) return;
      void navigate(
        withBackTarget(
          caseDetailPath(target.caseId, target.processModelId),
          backTo,
        ),
      );
    },
    [backTo, data, navigate],
  );

  /**
   * 要対応からの再開。
   *
   * 提出できるタスクがあるかに関わらず**案件詳細へ送る**。詳細の操作パネルで
   * そのまま提出できるので、同じ操作の画面を 2 つ持たない（入力画面は詳細の
   * 部分集合だった）。開けるタスクが無い差し戻し（終了する作り）でも、詳細で
   * 理由と履歴を確認できる。
   */
  const resumeCase = useCallback(
    (caseId: number) => {
      const target = actionRequired.data?.items.find(
        (item) => item.caseId === caseId,
      );
      if (target === undefined) return;

      const hasOpenTask = target.currentStep?.taskGuid != null;

      void navigate(
        withBackTarget(
          hasOpenTask
            ? caseActionPath(target.caseId, target.processModelId)
            : caseDetailPath(target.caseId, target.processModelId),
          backTo,
        ),
      );
    },
    [actionRequired.data, backTo, navigate],
  );

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold">
            {t("domain:myApplications")}
          </h1>
          {data ? (
            <p className="text-muted-foreground text-sm">
              {/* 再絞り込みしたときは API の総数が実件数より多いので断りを入れる */}
              {data.isApproximateTotal
                ? t("common:pagination.totalFiltered", {
                    count: data.items.length,
                  })
                : t("common:pagination.total", {
                    count: data.pagination.total,
                  })}
            </p>
          ) : null}
        </div>
        <Button asChild>
          <Link to={ROUTES.newApplication}>
            {t("common:newApplication.title")}
          </Link>
        </Button>
      </header>

      {/*
        対象範囲。承認者は承認リクエストからは処理済みが消え、全案件モニタは
        権限的に開けないため、これが「自分が承認した案件」への唯一の導線になる。
        `for-me` は元々「関与した案件」を返すので、起票者の絞り込みを外すだけ。
      */}
      <div
        role="group"
        aria-label={t("common:myApplications.scopeLabel")}
        className="flex gap-2"
      >
        <Button
          type="button"
          size="sm"
          variant={filters.involved === true ? "outline" : "default"}
          onClick={() => applyFilters({ ...filters, involved: false })}
        >
          {t("common:myApplications.scopeMine")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={filters.involved === true ? "default" : "outline"}
          onClick={() => applyFilters({ ...filters, involved: true })}
        >
          {t("common:myApplications.scopeInvolved")}
        </Button>
      </div>

      <ApplicationFilters filters={filters} onChange={applyFilters} />
      <SavedViewBar filters={filters} onApply={applyFilters} />

      {isPending ? <ListSkeleton /> : null}

      {isError ? (
        <p className="text-destructive text-sm">{t("common:state.error")}</p>
      ) : null}

      {data ? (
        <>
          <ActionRequiredSection
            items={actionRequired.data?.items ?? []}
            hasMore={actionRequired.data?.hasMore ?? false}
            onResume={resumeCase}
          />
          <section
            aria-labelledby="all-applications-heading"
            className="flex flex-col gap-3"
          >
            <h2 id="all-applications-heading" className="text-sm font-semibold">
              {t("common:myApplications.allApplications")}
            </h2>
            <MyApplicationTable
              items={data.items}
              onSelect={openCase}
              // 「関与した案件」では自分の申請と他人の申請が混ざるので起票者を出す
              showInitiator={filters.involved === true}
              currentUsername={user?.username ?? null}
            />
            <ListPagination
              page={page}
              pages={data.pagination.pages}
              onChange={applyPage}
            />
          </section>
        </>
      ) : null}
    </section>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-hidden="true">
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-9 w-full" />
    </div>
  );
}
